import PropTypes from 'prop-types';
import { memo, useCallback, useMemo } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import DateFilter from '../../../../Filters/DateSelectorFilter';
import { setFlightTripSearchInputs } from '../../../../../store/slices/tripSearch';
import flightSearchInput from '../../../../../helpers/selectors/flightSearchInput';
import { updateFlightSearchObj } from '../../../../../store/slices/flight';
import { getTravelPolicyLeadDays } from '../../../../../store/slices/user';
import { TravelTypes } from '../../../../../constants';

const DirectFlightDateFilter = ({
  tripId,
  dateFilterType,
  currentIndex,
  label: initialLabel,
  variant,
  minDate,
  maxDate,
}) => {
  const { track } = useAnalytics();
  const dispatch = useDispatch();
  const isTripSearch = !tripId;
  const { searchInputs } = useSelector(flightSearchInput(tripId, isTripSearch));
  const leadDays = useSelector(getTravelPolicyLeadDays(TravelTypes.flight));
  const currentSegment = searchInputs.flightSegments?.[currentIndex] || {};
  const currentValue = currentSegment[dateFilterType];

  const handleDateChange = useCallback(
    (value) => {
      const payload = {
        tripId,
        searchObj: {
          flightSegments: searchInputs.flightSegments ? [...searchInputs.flightSegments] : [],
          page: 1,
        },
      };

      payload.searchObj.flightSegments[currentIndex] = {
        ...payload.searchObj.flightSegments[currentIndex],
        [dateFilterType]: value,
      };

      if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
      else dispatch(updateFlightSearchObj(payload));
      track('directSellFlightSearchLeaveDateInput.handleChange', {
        [dateFilterType]: value,
        currentIndex,
        tripId,
      });
    },
    [
      tripId,
      searchInputs.flightSegments,
      currentIndex,
      dateFilterType,
      isTripSearch,
      dispatch,
      track,
    ],
  );

  const label = useMemo(() => {
    if (!_.isEmpty(currentValue)) {
      return dateFilterType === 'departureDate' ? 'Departure' : 'Arrival';
    }
    return initialLabel;
  }, [currentValue, dateFilterType, initialLabel]);

  return (
    <DateFilter
      onChange={(value) => handleDateChange(value)}
      label={currentValue ? '' : label}
      filterButtonMaxWidth="w-full"
      value={currentValue}
      defaultValue={currentValue}
      variant={variant}
      bgClassName="bg-searchInput-0"
      textColorClassName="text-datepicker-0"
      leadDays={leadDays}
      minDate={minDate}
      maxDate={maxDate}
    />
  );
};

DirectFlightDateFilter.propTypes = {
  tripId: PropTypes.string,
  dateFilterType: PropTypes.string.isRequired,
  label: PropTypes.string,
  currentIndex: PropTypes.number.isRequired,
  variant: PropTypes.string,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
};

DirectFlightDateFilter.defaultProps = {
  tripId: undefined,
  label: undefined,
  variant: 'outline-no-radius',
  minDate: undefined,
  maxDate: undefined,
};

export default memo(DirectFlightDateFilter);
